import mergeSections from '@/components/ProductProfile/utils/drawSection/mergeSections'
import createTargetObjectCanvas from '@/components/ProductProfile/utils/drawSection/createTargetObjectCanvas'
import applyCanvasOverlayWithShader from '@/components/ProductProfile/utils/drawSection/applyShaderOnMaterial'
import clearCanvasOverlay from '@/components/ProductProfile/utils/drawSection/clearCanvasOverlay'
import updateFrameSections from '@/components/ProductProfile/utils/drawSection/updateFrameSections'

export default function updateAllTargetObjects(sections: any[], sectionId: string | null = null): void {
  if (!(window as any).threeViewer?.product) return

  // Frame profiles take an entirely separate route — one projector per section instead of
  // one unrolled canvas per mesh. Everything below this guard is the cylindrical path,
  // unchanged, and is what the 98 cylindrical profiles still run.
  if ((window as any).threeViewer?.sectionModel === 'frame') {
    updateFrameSections(sections, sectionId)
    return
  }

  const targetObjectNames = new Set(
    sections
      ?.filter((section) => section.targetObject && section.targetObject?.name)
      .map((section) => section.targetObject.name)
  )

  // Clear overlays for target objects that are no longer in use
  const previousTargetObjectNames = (window as any).threeViewer.targetObjectNames as Set<string> | undefined
  if (previousTargetObjectNames) {
    previousTargetObjectNames.forEach((name: string) => {
      if (!targetObjectNames.has(name)) {
        clearCanvasOverlay(name)
      }
    })
  }

  ;(window as any).threeViewer.targetObjectNames = targetObjectNames

  // The shader stage only receives a target object name, so publish the material
  // each name resolves to on the same bus the rest of the 3D state uses. Several
  // sections can share a target object; the first one carrying a material wins,
  // matching how the customizer picks its section.
  const sectionMaterials: Record<string, { type: string; file: string | null; color: string | null }> = {}
  sections?.forEach((section: any) => {
    const targetName = section.targetObject?.name
    if (!targetName || sectionMaterials[targetName]) return
    const type = section.materialType?.value || section.materialType
    if (!type) return
    const source = section.materialSource
    const usesPattern = source?.mode === 'pattern'
    sectionMaterials[targetName] = {
      type,
      file: usesPattern ? source?.file?.string || source?.file?.url || null : null,
      color: usesPattern ? null : source?.color || null,
    }
  })
  ;(window as any).threeViewer.sectionMaterials = sectionMaterials
  targetObjectNames.forEach(async (name: string) => {
    const data = mergeSections(name, sections)

    if (!data) return

    const hasFront = data.items && data.items.length > 0
    const hasBack = data.backItems && data.backItems.length > 0

    // If there are no items to draw (sections without width/height), clear the overlay
    if (!hasFront && !hasBack) {
      clearCanvasOverlay(name)
      return
    }

    // Create front canvas
    if (hasFront) {
      await createTargetObjectCanvas({ ...data, singleSectionId: sectionId })
    }

    // Create back canvas (dynamically created if needed)
    if (hasBack) {
      await createTargetObjectCanvas({
        name: `${name}-back`,
        items: data.backItems,
        size: data.size,
        singleSectionId: sectionId,
      })
    } else {
      // Remove back canvas if no back items
      const backCanvas = document.querySelector(`#targetObjectCanvas-${name}-back`)
      if (backCanvas) backCanvas.remove()
    }

    await applyCanvasOverlayWithShader(name)
  })
}
